import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { AddRounded, DeleteOutlineRounded, EditCalendarRounded } from '@mui/icons-material';
import { isEditableSchedule, scheduleProblem, type ScheduleEntry } from '@dosfilos/domain';
import { useTenantTimeFormat, useTenantTimeZone } from '../../../stores/tenant.store';
import {
  blockWarnings,
  nextBlock,
  timeOptionsFor,
  type BlockWarning,
} from './section-schedule.model';
import { wallTime } from '../../portal-v2/util/classroom';

// ─── El horario de una sección (H7) ─────────────────────────────────────────
// `value === null` significa SIN TOCAR: al guardar no se envía y el servidor
// deja el horario como estaba. Es lo que protege a los horarios importados que
// este editor no sabe leer (LABTS trae solo la hora de inicio: término =
// inicio): se muestran tal cual y no se reescriben salvo que alguien pulse
// «Editar horario».
//
// Las horas son de reloj, en la zona horaria del seminario, y se muestran en su
// formato (12 o 24 horas). El valor que viaja es siempre HH:mm.

/** Claves literales (el guardián de i18n busca cada clave escrita entera). */
const WEEKDAY_KEYS = [
  'portal.syllabus.weekdays.sunday',
  'portal.syllabus.weekdays.monday',
  'portal.syllabus.weekdays.tuesday',
  'portal.syllabus.weekdays.wednesday',
  'portal.syllabus.weekdays.thursday',
  'portal.syllabus.weekdays.friday',
  'portal.syllabus.weekdays.saturday',
] as const;

/** La semana como la lee un seminario: de lunes a domingo. */
const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0] as const;

const PROBLEM_KEYS: Record<string, string> = {
  'section.schedule_invalid_day': 'common:errors.section.schedule_invalid_day',
  'section.schedule_invalid_time': 'common:errors.section.schedule_invalid_time',
  'section.schedule_end_before_start': 'common:errors.section.schedule_end_before_start',
};

const WARNING_KEYS: Record<BlockWarning, string> = {
  early_start: 'sections.schedule.warnEarly',
  long_block: 'sections.schedule.warnLong',
};

export interface SectionScheduleEditorProps {
  /** El horario que la sección tiene hoy ([] al crear). */
  original: readonly ScheduleEntry[];
  /** `null` = sin tocar (no se envía al guardar). */
  value: ScheduleEntry[] | null;
  onChange: (next: ScheduleEntry[] | null) => void;
}

/** El problema del horario en edición, o null. El diálogo lo usa para no
 *  dejar guardar un horario incompleto. */
export function scheduleDraftProblem(value: ScheduleEntry[] | null): string | null {
  return value === null ? null : scheduleProblem(value);
}

export function SectionScheduleEditor({ original, value, onChange }: SectionScheduleEditorProps) {
  const { t } = useTranslation('sis');
  const timeFmt = useTenantTimeFormat();
  const timeZone = useTenantTimeZone();

  const dayLabel = (d: number) => t(WEEKDAY_KEYS[d] ?? WEEKDAY_KEYS[0]);

  /** Una línea tal como está guardada. Si término = inicio (la forma
   *  importada), se muestra solo el inicio en vez de inventar un término. */
  const lineOf = (s: ScheduleEntry) => {
    const day = dayLabel(s.dayOfWeek);
    const start = wallTime(s.startTime, timeFmt);
    const text =
      s.endTime && s.endTime !== s.startTime
        ? t('sections.schedule.line', { day, start, end: wallTime(s.endTime, timeFmt) })
        : t('sections.schedule.lineStartOnly', { day, start });
    return s.location ? `${text} · ${s.location}` : text;
  };

  const zoneNote = (
    <Typography variant="caption" color="text.secondary">
      {t('sections.schedule.zoneNote', { zone: timeZone })}
    </Typography>
  );

  // ── Sin tocar: se muestra lo que hay ────────────────────────────────────
  if (value === null) {
    const editable = isEditableSchedule([...original]);
    return (
      <Stack spacing={1}>
        <Typography variant="subtitle2">{t('sections.schedule.title')}</Typography>
        {original.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('sections.schedule.empty')}
          </Typography>
        ) : (
          original.map((s, i) => (
            <Typography key={i} variant="body2">
              {lineOf(s)}
            </Typography>
          ))
        )}
        {original.length > 0 && !editable && (
          <Alert severity="info" variant="outlined">
            {t('sections.schedule.importedNote')}
          </Alert>
        )}
        <Box>
          <Button
            size="small"
            variant="outlined"
            startIcon={original.length === 0 ? <AddRounded /> : <EditCalendarRounded />}
            onClick={() =>
              onChange(
                original.length === 0
                  ? [nextBlock(undefined)]
                  : // Un término que no es posterior al inicio (la forma importada)
                    // se deja vacío: hay que completarlo, no heredarlo.
                    original.map((s) => ({
                      ...s,
                      endTime: s.endTime > s.startTime ? s.endTime : '',
                    })),
              )
            }
          >
            {original.length === 0 ? t('sections.schedule.add') : t('sections.schedule.edit')}
          </Button>
        </Box>
        {original.length > 0 && zoneNote}
      </Stack>
    );
  }

  // ── En edición ──────────────────────────────────────────────────────────
  const problem = scheduleProblem(value);
  const update = (i: number, patch: Partial<ScheduleEntry>) =>
    onChange(value.map((s, j) => (j === i ? { ...s, ...patch } : s)));

  // La lista empieza a las 06:00 (M1·H7b): ver section-schedule.model.
  const timeOptions = timeOptionsFor;

  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2">{t('sections.schedule.title')}</Typography>
      {value.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {t('sections.schedule.emptyWillClear')}
        </Typography>
      )}
      {value.map((s, i) => (
        <Stack key={i} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-start">
          <TextField
            select
            size="small"
            label={t('sections.schedule.day')}
            value={s.dayOfWeek}
            onChange={(e) => update(i, { dayOfWeek: Number(e.target.value) })}
            sx={{ minWidth: 130 }}
          >
            {DAY_ORDER.map((d) => (
              <MenuItem key={d} value={d}>
                {dayLabel(d)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label={t('sections.schedule.start')}
            value={s.startTime}
            onChange={(e) => update(i, { startTime: e.target.value })}
            sx={{ minWidth: 120 }}
          >
            {timeOptions(s.startTime).map((h) => (
              <MenuItem key={h} value={h}>
                {wallTime(h, timeFmt)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label={t('sections.schedule.end')}
            value={s.endTime}
            onChange={(e) => update(i, { endTime: e.target.value })}
            sx={{ minWidth: 120 }}
          >
            {timeOptions(s.endTime).map((h) => (
              <MenuItem key={h} value={h}>
                {wallTime(h, timeFmt)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label={t('sections.schedule.location')}
            value={s.location ?? ''}
            onChange={(e) => {
              // Vacío = sin lugar: la clave se quita, no se guarda «».
              const text = e.target.value;
              const next: ScheduleEntry = {
                dayOfWeek: s.dayOfWeek,
                startTime: s.startTime,
                endTime: s.endTime,
                ...(text ? { location: text } : {}),
              };
              onChange(value.map((x, j) => (j === i ? next : x)));
            }}
            sx={{ flex: 1, minWidth: 120 }}
          />
          <IconButton
            aria-label={t('sections.schedule.removeBlock')}
            onClick={() => onChange(value.filter((_, j) => j !== i))}
          >
            <DeleteOutlineRounded fontSize="small" />
          </IconButton>
        </Stack>
      ))}
      {/* Avisos, no errores: guardar sigue permitido. */}
      {value.flatMap((s, i) =>
        blockWarnings(s).map((w) => (
          <Typography key={`${i}-${w}`} variant="caption" sx={{ color: 'warning.main' }}>
            {t(WARNING_KEYS[w], { block: i + 1 })}
          </Typography>
        )),
      )}
      {problem && value.length > 0 && (
        <Typography variant="caption" color="error">
          {t(PROBLEM_KEYS[problem] ?? 'common:errors.section.schedule_invalid_time')}
        </Typography>
      )}
      <Stack direction="row" spacing={1}>
        <Button
          size="small"
          startIcon={<AddRounded />}
          onClick={() => onChange([...value, nextBlock(value.at(-1))])}
        >
          {t('sections.schedule.addBlock')}
        </Button>
        <Button size="small" color="inherit" onClick={() => onChange(null)}>
          {t('sections.schedule.discard')}
        </Button>
      </Stack>
      {zoneNote}
    </Stack>
  );
}
